import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { localeSupportsRecognitionText } from '../config/localeRecognition'
import type { LineGenerationConfig, TdLine, PriceListSummary } from '../types/testdata.types'
import {
  PRECISION_LINE_COUNT_MAX, PRECISION_LINE_COUNT_MIN,
  PRECISION_QUANTITY_DIGITS_MAX, PRECISION_QUANTITY_DIGITS_MIN,
  PRECISION_UNIT_PRICE_DIGITS_MAX, PRECISION_UNIT_PRICE_DIGITS_MIN,
} from '../types/testdata.types'
import { formatDecimalLiteral, parseDecimalLiteral } from '../utils/decimalLiteral'

// ---------------------------------------------------------------------------
// Helpers (local to this component)
// ---------------------------------------------------------------------------

function calcNet(lines: TdLine[]): number {
  return lines.reduce((sum, l) => sum + l.quantity * l.unit_price, 0)
}

function calcVat(lines: TdLine[]): number {
  return lines.reduce((sum, l) => sum + l.quantity * l.unit_price * l.vat_percentage / 100, 0)
}

function calcVatByRate(lines: TdLine[]): { rate: number; grondslag: number; tax: number }[] {
  const map = new Map<number, { grondslag: number; tax: number }>()
  for (const l of lines) {
    const base = l.quantity * l.unit_price
    const tax = base * l.vat_percentage / 100
    const existing = map.get(l.vat_percentage) ?? { grondslag: 0, tax: 0 }
    map.set(l.vat_percentage, { grondslag: existing.grondslag + base, tax: existing.tax + tax })
  }
  return Array.from(map.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([rate, v]) => ({ rate, ...v }))
}

function makeFmt(locale: string) {
  return (v: number) => v.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const inputCls = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
const selectCls = inputCls

/** REQ-TESTDATA-004 (v0.8.1) — decimal-literal-preserving input for a
 * materialized precision-test cell (quantity/unit price with a known
 * intended scale). Unlike `<input type="number">`, this never normalizes
 * away trailing zeros or rejects a locale comma decimal separator: the
 * displayed text is a local "draft" that only snaps back to the canonical
 * `value`/`scale`-derived literal on blur, so mid-typing states like `12,`
 * are not fought by a re-render. Every valid intermediate parse commits
 * immediately via `onCommit`. */
function DecimalLiteralInput({
  value, scale, locale, onCommit, className, disabled,
}: {
  value: number
  scale: number
  locale: string
  onCommit: (value: number, scale: number) => void
  className: string
  disabled?: boolean
}) {
  const [draft, setDraft] = useState(() => formatDecimalLiteral(value, scale, locale))
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!focused) setDraft(formatDecimalLiteral(value, scale, locale))
  }, [value, scale, locale, focused])

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      value={draft}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onChange={e => {
        setDraft(e.target.value)
        const parsed = parseDecimalLiteral(e.target.value, locale)
        if (parsed) onCommit(parsed.value, parsed.scale)
      }}
      onBlur={() => {
        setFocused(false)
        setDraft(formatDecimalLiteral(value, scale, locale))
      }}
    />
  )
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface LinesEditorProps {
  lines: TdLine[]
  isDA: boolean
  isInvoice: boolean
  locale: string
  vatCodeOptions: Array<{ value: string; label: string }>
  availablePriceLists: PriceListSummary[]
  randomPriceListId: number | null
  onRandomPriceListChange: (id: number) => void
  randomCount: number
  onRandomCountChange: (count: number) => void
  randomLoading: boolean
  randomError: string | null
  roundUnitPrices: boolean
  onToggleRoundUnitPrices: (checked: boolean) => void
  roundUnitPriceValue: number
  onRoundUnitPriceValueChange: (value: number) => void
  compactPdfLines: boolean
  onToggleCompactPdfLines: (checked: boolean) => void
  includeLocaleRecognitionText: boolean
  onToggleIncludeLocaleRecognitionText: (checked: boolean) => void
  isFacturx: boolean
  isSetMode: boolean
  includeGroundTruth: boolean
  onToggleIncludeGroundTruth: (checked: boolean) => void
  lineGeneration: LineGenerationConfig
  onLineGenerationChange: (update: Partial<LineGenerationConfig>) => void
  // REQ-TESTDATA-004 (v0.8.1)
  precisionMaterialized: boolean
  isPrecisionStale: boolean
  onUpdateLineDecimal: (idx: number, field: 'quantity' | 'unit_price', value: number, scale: number) => void
  vatOverrides: Array<{ code: string; percentage: number }>
  showVatAdd: boolean
  onToggleVatAdd: (show: boolean) => void
  newVatCode: string
  onNewVatCodeChange: (code: string) => void
  newVatPct: number
  onNewVatPctChange: (pct: number) => void
  onAddVatOverride: () => void
  onRemoveVatOverride: (idx: number) => void
  onClearVatOverrides: () => void
  onGenerateRandomLines: () => void
  onUpdateLine: (idx: number, key: keyof TdLine, val: string | number) => void
  onAddLine: () => void
  onRemoveLine: (idx: number) => void
  // REQ-TESTDATA-007 (CAP-003 v1.2) — when set, `lines` are a materialized
  // Financial Scenario's canonical rows: read-only (no add/delete/edit/
  // regenerate), with a clear text+icon locked indicator naming the active
  // scenario (never colour alone). `null`/absent = ordinary Normal-mode
  // behaviour, completely unchanged.
  //
  // Hardening (UI hotfix, 2026-09-17): the caller passes the i18n KEY, never
  // a pre-resolved value. Translation happens in exactly one place below
  // (`t(scenarioLock.labelKey)`), which react-i18next's `TFunction` always
  // returns as a plain `string` — this makes it structurally impossible for
  // a ReactNode, icon object, or option object to ever reach the
  // `scenario_locked` interpolation, which a caller-supplied pre-resolved
  // `label` value could not guarantee.
  scenarioLock?: { scenarioId: string; labelKey: string } | null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LinesEditor({
  lines, isDA, isInvoice, locale, vatCodeOptions,
  availablePriceLists, randomPriceListId, onRandomPriceListChange,
  randomCount, onRandomCountChange, randomLoading, randomError,
  roundUnitPrices, onToggleRoundUnitPrices, roundUnitPriceValue, onRoundUnitPriceValueChange,
  compactPdfLines, onToggleCompactPdfLines,
  includeLocaleRecognitionText, onToggleIncludeLocaleRecognitionText,
  isFacturx, isSetMode, includeGroundTruth, onToggleIncludeGroundTruth,
  lineGeneration, onLineGenerationChange, precisionMaterialized, isPrecisionStale, onUpdateLineDecimal,
  vatOverrides, showVatAdd, onToggleVatAdd,
  newVatCode, onNewVatCodeChange, newVatPct, onNewVatPctChange,
  onAddVatOverride, onRemoveVatOverride, onClearVatOverrides, onGenerateRandomLines,
  onUpdateLine, onAddLine, onRemoveLine,
  scenarioLock,
}: LinesEditorProps) {
  const { t } = useTranslation()
  const fmt = makeFmt(locale)
  const net = calcNet(lines)
  const vat = calcVat(lines)
  const vatByRate = calcVatByRate(lines)
  const isPrecisionMode = lineGeneration.mode === 'precision_test'
  const locked = !!scenarioLock

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">{t('linesEditor.article_lines')}</h3>
      </div>

      {/* REQ-TESTDATA-007 (CAP-003 v1.2) — scenario-locked indicator: text +
          icon, never colour alone, per the accessibility baseline. */}
      {scenarioLock && (
        <div
          className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-xs font-medium"
          role="status"
        >
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>{t('linesEditor.scenario_locked', { scenario: t(scenarioLock.labelKey) })}</span>
        </div>
      )}

      {/* Ronde stukprijzen — voorkomt afrondingsverschillen bij veel regels */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700/50">
        <label className={`flex items-center gap-2 select-none ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={roundUnitPrices}
            disabled={locked}
            onChange={e => onToggleRoundUnitPrices(e.target.checked)}
            className="w-4 h-4 rounded accent-teal-600 disabled:cursor-not-allowed"
          />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('linesEditor.round_unit_prices_label')}</span>
        </label>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min={0.01}
            step={0.01}
            value={roundUnitPriceValue}
            disabled={!roundUnitPrices || locked}
            onChange={e => onRoundUnitPriceValueChange(parseFloat(e.target.value) || 1)}
            className="w-20 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 text-right focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50"
          />
          <span className="text-xs text-gray-400">{t('linesEditor.round_unit_prices_unit')}</span>
        </div>
        {roundUnitPrices && (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">{t('linesEditor.round_unit_prices_hint')}</span>
        )}
      </div>

      {/* Compacte PDF-regels — voorkomt dat lange omschrijvingen de PDF verlengen */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700/50">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={compactPdfLines}
            onChange={e => onToggleCompactPdfLines(e.target.checked)}
            className="w-4 h-4 rounded accent-teal-600"
          />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('linesEditor.compact_pdf_lines_label')}</span>
        </label>
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">{t('linesEditor.compact_pdf_lines_hint')}</span>
      </div>

      {/* CAP-013 / REQ-TESTREF v0.2 — locale recognition test text, alleen relevant voor invoice */}
      {isInvoice && (
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700/50">
          <label
            className={`flex items-center gap-2 select-none ${localeSupportsRecognitionText(locale) ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            title={localeSupportsRecognitionText(locale) ? undefined : t('linesEditor.locale_recognition_text_unsupported')}
          >
            <input
              type="checkbox"
              checked={includeLocaleRecognitionText}
              disabled={!localeSupportsRecognitionText(locale)}
              onChange={e => onToggleIncludeLocaleRecognitionText(e.target.checked)}
              className="w-4 h-4 rounded accent-teal-600 disabled:opacity-50"
            />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('linesEditor.locale_recognition_text_label')}</span>
          </label>
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            {localeSupportsRecognitionText(locale)
              ? t('linesEditor.locale_recognition_text_hint')
              : t('linesEditor.locale_recognition_text_unsupported')}
          </span>
        </div>
      )}

      {/* CAP-013 / REQ-TESTREF-010 — centrale Ground Truth-keuze: bij downloaden
          een aparte *_GROUND-TRUTH.pdf naast de primaire ZIP-inhoud, bij mailen
          een aparte PDF-bijlage. Nooit meer gemerged met de primaire PDF.
          Verborgen in set-modus (foutinjectie/debet-credit): die downloadpaden
          negeren dit veld — de toggle zou daar geen effect hebben. */}
      {isInvoice && !isSetMode && (
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700/50">
          <label
            className={`flex items-center gap-2 select-none ${isFacturx ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            title={isFacturx ? (t('linesEditor.ground_truth_unsupported_facturx') as string) : undefined}
          >
            <input
              type="checkbox"
              checked={includeGroundTruth}
              disabled={isFacturx}
              onChange={e => onToggleIncludeGroundTruth(e.target.checked)}
              className="w-4 h-4 rounded accent-amber-600 disabled:opacity-50"
            />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('linesEditor.ground_truth_label')}</span>
          </label>
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            {isFacturx ? t('linesEditor.ground_truth_unsupported_facturx') : t('linesEditor.ground_truth_hint')}
          </span>
        </div>
      )}

      {/* REQ-TESTDATA-003 (v0.8) — optional opt-in precision-test line generation.
          Hidden in set mode (foutinjectie/debet-credit): those flows don't route
          through the precision transform, so the toggle would silently no-op.
          REQ-TESTDATA-007: disabled while a Financial Scenario is locked —
          Precision Test is instead composed via the scenario preview itself
          (its own line_generation config), never by regenerating rows here. */}
      {!isSetMode && (
        <div className="flex flex-col gap-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700/50">
          <label className={`flex items-center gap-2 select-none ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={lineGeneration.mode === 'precision_test'}
              disabled={locked}
              onChange={e => onLineGenerationChange({ mode: e.target.checked ? 'precision_test' : 'catalogue' })}
              className="w-4 h-4 rounded accent-purple-600 disabled:cursor-not-allowed"
            />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('linesEditor.precision_test_label')}</span>
          </label>
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">{t('linesEditor.precision_test_hint')}</span>

          {lineGeneration.mode === 'precision_test' && (
            <div className="flex flex-wrap items-end gap-3 mt-1 pl-6">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">{t('linesEditor.precision_line_count')}</span>
                <input
                  type="number"
                  min={PRECISION_LINE_COUNT_MIN}
                  max={PRECISION_LINE_COUNT_MAX}
                  value={lineGeneration.line_count ?? ''}
                  placeholder={String(lines.length)}
                  onChange={e => onLineGenerationChange({
                    line_count: e.target.value === '' ? null : Math.min(PRECISION_LINE_COUNT_MAX, Math.max(PRECISION_LINE_COUNT_MIN, parseInt(e.target.value) || PRECISION_LINE_COUNT_MIN)),
                  })}
                  className="w-20 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">{t('linesEditor.precision_quantity_mode')}</span>
                <select
                  value={lineGeneration.quantity_mode}
                  onChange={e => onLineGenerationChange({ quantity_mode: e.target.value as 'whole' | 'decimal' })}
                  className={selectCls}
                >
                  <option value="whole">{t('linesEditor.precision_quantity_whole')}</option>
                  <option value="decimal">{t('linesEditor.precision_quantity_decimal')}</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">{t('linesEditor.precision_quantity_decimals')}</span>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={PRECISION_QUANTITY_DIGITS_MIN}
                    max={lineGeneration.quantity_precision_max}
                    disabled={lineGeneration.quantity_mode !== 'decimal'}
                    value={lineGeneration.quantity_precision_min}
                    onChange={e => onLineGenerationChange({ quantity_precision_min: Math.max(PRECISION_QUANTITY_DIGITS_MIN, parseInt(e.target.value) || 0) })}
                    className="w-14 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-400">–</span>
                  <input
                    type="number"
                    min={lineGeneration.quantity_precision_min}
                    max={PRECISION_QUANTITY_DIGITS_MAX}
                    disabled={lineGeneration.quantity_mode !== 'decimal'}
                    value={lineGeneration.quantity_precision_max}
                    onChange={e => onLineGenerationChange({ quantity_precision_max: Math.min(PRECISION_QUANTITY_DIGITS_MAX, parseInt(e.target.value) || 0) })}
                    className="w-14 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">{t('linesEditor.precision_price_decimals')}</span>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={PRECISION_UNIT_PRICE_DIGITS_MIN}
                    max={lineGeneration.unit_price_precision_max}
                    value={lineGeneration.unit_price_precision_min}
                    onChange={e => onLineGenerationChange({ unit_price_precision_min: Math.max(PRECISION_UNIT_PRICE_DIGITS_MIN, parseInt(e.target.value) || 2) })}
                    className="w-14 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-xs text-gray-400">–</span>
                  <input
                    type="number"
                    min={lineGeneration.unit_price_precision_min}
                    max={PRECISION_UNIT_PRICE_DIGITS_MAX}
                    value={lineGeneration.unit_price_precision_max}
                    onChange={e => onLineGenerationChange({ unit_price_precision_max: Math.min(PRECISION_UNIT_PRICE_DIGITS_MAX, parseInt(e.target.value) || 2) })}
                    className="w-14 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none pb-1.5">
                <input
                  type="checkbox"
                  checked={lineGeneration.vary_precision_per_line}
                  onChange={e => onLineGenerationChange({ vary_precision_per_line: e.target.checked })}
                  className="w-4 h-4 rounded accent-purple-600"
                />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('linesEditor.precision_vary_per_line')}</span>
              </label>
            </div>
          )}

          {/* REQ-TESTDATA-004 §5/§8 — explicit stale-preview state: never
              silently submit rows generated from an older config. Text +
              icon cue (not color alone) per the accessibility baseline. */}
          {isPrecisionMode && (precisionMaterialized || isPrecisionStale) && (
            <div
              className={`flex items-center gap-2 mt-1 pl-6 text-xs font-medium ${
                isPrecisionStale ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
              }`}
              role="status"
            >
              {isPrecisionStale ? (
                <>
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  {t('linesEditor.precision_preview_stale')}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('linesEditor.precision_preview_current')}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {availablePriceLists.length === 0 && !locked && (
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700/50">
          <button
            onClick={onAddLine}
            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-medium border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            {t('linesEditor.add_line')}
          </button>
        </div>
      )}

      {availablePriceLists.length > 0 && !locked && (
        <>
          {/* Willekeurige regels uit prijslijst */}
          <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700/50">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">{t('linesEditor.generate_from_pricelist')}</span>
            <select
              value={randomPriceListId ?? ''}
              onChange={e => onRandomPriceListChange(Number(e.target.value))}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {availablePriceLists.map(pl => (
                <option key={pl.id} value={pl.id}>{pl.name} ({pl.row_count} {t('linesEditor.articles')})</option>
              ))}
            </select>
            {/* REQ-TESTDATA-004 §3 — while Precision test is on, its own
                `Aantal regels` (in the precision block above) is the ONE
                authoritative line count; this separate catalogue count is
                hidden rather than left as a second, conflicting count. */}
            {!isPrecisionMode && (
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min={1}
                  value={randomCount}
                  onChange={e => onRandomCountChange(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-teal-500"
                  title={t('linesEditor.line_count')}
                />
                <span className="text-xs text-gray-400">{t('linesEditor.lines')}</span>
              </div>
            )}
            <button
              onClick={onGenerateRandomLines}
              disabled={randomLoading}
              className="flex items-center gap-1.5 text-xs font-medium text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700 rounded-lg px-3 py-1.5 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {randomLoading ? (
                <>
                  <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  {t('common.busy')}
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {isPrecisionMode ? t('linesEditor.generate_precision_lines') : t('linesEditor.generate_lines')}
                </>
              )}
            </button>
            {/* REQ-TESTDATA-004 §11 — "Regel toevoegen" grouped with the other
                line-table actions instead of floating at the top-right of the
                whole card. */}
            <button
              onClick={onAddLine}
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-medium border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              {t('linesEditor.add_line')}
            </button>
            {randomError && (
              <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                {randomError}
              </span>
            )}
          </div>

          {/* BTW-instellingen voor gegenereerde regels */}
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700/50">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">{t('linesEditor.vat_testset')}</span>

            {vatOverrides.length === 0 && !showVatAdd && (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">{t('linesEditor.from_pricelist')}</span>
            )}

            {vatOverrides.map((v, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 rounded-full px-2.5 py-0.5"
              >
                {v.code} — {v.percentage}%
                <button
                  onClick={() => onRemoveVatOverride(i)}
                  className="ml-0.5 text-indigo-400 hover:text-red-500 transition-colors leading-none"
                  title={t('common.delete')}
                >
                  ×
                </button>
              </span>
            ))}

            {showVatAdd ? (
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1">
                <select
                  value={newVatCode}
                  onChange={e => onNewVatCodeChange(e.target.value)}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {vatCodeOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={newVatPct}
                  onChange={e => onNewVatPctChange(parseFloat(e.target.value) || 0)}
                  className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 px-1.5 py-1 text-right focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  title={t('linesEditor.vat_rate_pct')}
                />
                <span className="text-xs text-gray-400">%</span>
                <button
                  onClick={onAddVatOverride}
                  className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 transition-colors"
                >
                  {t('common.add')}
                </button>
                <button
                  onClick={() => onToggleVatAdd(false)}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <button
                onClick={() => onToggleVatAdd(true)}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 border border-indigo-200 dark:border-indigo-700 rounded-full px-2.5 py-0.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
              >
                {t('linesEditor.add_rate')}
              </button>
            )}

            {vatOverrides.length > 1 && (
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                {t('linesEditor.vat_hint_multiple')}
              </span>
            )}
            {vatOverrides.length === 1 && (
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                {t('linesEditor.vat_hint_single')}
              </span>
            )}
            {vatOverrides.length > 0 && (
              <button
                onClick={onClearVatOverrides}
                className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors ml-1"
                title={t('linesEditor.clear_vat_settings')}
              >
                {t('linesEditor.clear_all')}
              </button>
            )}
          </div>
        </>
      )}

      {/* Tabel */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-center text-xs font-medium text-gray-500 pb-2 pr-2 w-10">{t('linesEditor.col_nr')}</th>
              <th className="text-left text-xs font-medium text-gray-500 pb-2 pr-2 w-28">{t('linesEditor.col_article')}</th>
              <th className="text-left text-xs font-medium text-gray-500 pb-2 pr-2 w-24">{t('linesEditor.col_gtin')}</th>
              <th className="text-left text-xs font-medium text-gray-500 pb-2 pr-2">{t('linesEditor.col_description')}</th>
              <th className="text-right text-xs font-medium text-gray-500 pb-2 pr-2 w-36">{t('linesEditor.col_quantity')}</th>
              <th className="text-left text-xs font-medium text-gray-500 pb-2 pr-2 w-14">{t('linesEditor.col_unit')}</th>
              {!isDA && <th className="text-right text-xs font-medium text-gray-500 pb-2 pr-2 w-36">{t('linesEditor.col_price')}</th>}
              {!isDA && <th className="text-center text-xs font-medium text-gray-500 pb-2 pr-2 w-20">{t('linesEditor.col_vat_code')}</th>}
              {!isDA && <th className="text-right text-xs font-medium text-gray-500 pb-2 pr-2 w-16">{t('linesEditor.col_vat_pct')}</th>}
              {!isDA && <th className="text-right text-xs font-medium text-gray-500 pb-2 pr-2 w-22">{t('linesEditor.col_vat_amount')}</th>}
              {!isDA && <th className="text-right text-xs font-medium text-gray-500 pb-2 w-24">{t('linesEditor.col_net')}</th>}
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((ln, idx) => {
              const lineNet = ln.quantity * ln.unit_price
              const lineVat = lineNet * ln.vat_percentage / 100
              return (
                <tr key={idx} className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-1.5 pr-2 text-center text-xs font-mono text-gray-400 dark:text-gray-500 select-none">
                    {String(idx + 1).padStart(2, '0')}
                  </td>
                  <td className="py-1.5 pr-2">
                    <input className={`${inputCls} disabled:opacity-70 disabled:cursor-not-allowed`} value={ln.article_number} disabled={locked} onChange={e => onUpdateLine(idx, 'article_number', e.target.value)} placeholder="ART-001" />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input className={`${inputCls} disabled:opacity-70 disabled:cursor-not-allowed`} value={ln.gtin} disabled={locked} onChange={e => onUpdateLine(idx, 'gtin', e.target.value)} placeholder="054123…" />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input className={`${inputCls} disabled:opacity-70 disabled:cursor-not-allowed`} value={ln.description} disabled={locked} onChange={e => onUpdateLine(idx, 'description', e.target.value)} placeholder={t('linesEditor.placeholder_description')} />
                  </td>
                  <td className="py-1.5 pr-2">
                    {/* REQ-TESTDATA-004 §12/§13 — a materialized precision line
                        (quantity_scale set) uses a locale-aware, literal-preserving
                        text input so trailing zeros/exact scale stay visible;
                        an ordinary catalogue-mode line (scale null) keeps the
                        existing plain number input unchanged. REQ-TESTDATA-007:
                        both disabled while scenario-locked. */}
                    {ln.quantity_scale != null ? (
                      <DecimalLiteralInput
                        value={ln.quantity}
                        scale={ln.quantity_scale}
                        locale={locale}
                        onCommit={(value, scale) => onUpdateLineDecimal(idx, 'quantity', value, scale)}
                        className={`${inputCls} text-right min-w-[9rem] ${locked ? 'opacity-70 cursor-not-allowed' : ''}`}
                        disabled={locked}
                      />
                    ) : (
                      <input type="number" min="0.001" step="0.001" disabled={locked} className={`${inputCls} text-right min-w-[9rem] disabled:opacity-70 disabled:cursor-not-allowed`} value={ln.quantity} onChange={e => onUpdateLine(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    <input className={`${inputCls} disabled:opacity-70 disabled:cursor-not-allowed`} value={ln.unit} disabled={locked} onChange={e => onUpdateLine(idx, 'unit', e.target.value)} placeholder="ST" />
                  </td>
                  {!isDA && (
                    <td className="py-1.5 pr-2">
                      {ln.unit_price_scale != null ? (
                        <DecimalLiteralInput
                          value={ln.unit_price}
                          scale={ln.unit_price_scale}
                          locale={locale}
                          onCommit={(value, scale) => onUpdateLineDecimal(idx, 'unit_price', value, scale)}
                          className={`${inputCls} text-right min-w-[9rem] ${roundUnitPrices || locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={locked}
                        />
                      ) : (
                        <input
                          type="number" min="0" step="0.01"
                          className={`${inputCls} text-right min-w-[9rem] ${roundUnitPrices || locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          value={ln.unit_price}
                          disabled={roundUnitPrices || locked}
                          title={roundUnitPrices ? (t('linesEditor.round_unit_prices_locked_title') as string) : undefined}
                          onChange={e => onUpdateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                        />
                      )}
                    </td>
                  )}
                  {!isDA && (
                    <td className="py-1.5 pr-2">
                      <select
                        className={`${selectCls} disabled:opacity-70 disabled:cursor-not-allowed`}
                        value={ln.vat_code}
                        disabled={locked}
                        onChange={e => onUpdateLine(idx, 'vat_code', e.target.value)}
                      >
                        {vatCodeOptions.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                  )}
                  {!isDA && (
                    <td className="py-1.5 pr-2">
                      <input type="number" min="0" max="100" step="0.1" disabled={locked} className={`${inputCls} text-right disabled:opacity-70 disabled:cursor-not-allowed`} value={ln.vat_percentage} onChange={e => onUpdateLine(idx, 'vat_percentage', parseFloat(e.target.value) || 0)} />
                    </td>
                  )}
                  {!isDA && (
                    <td className="py-1.5 text-right text-gray-500 dark:text-gray-400 font-mono text-xs whitespace-nowrap pr-2">
                      {fmt(lineVat)}
                    </td>
                  )}
                  {!isDA && (
                    <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 font-mono text-xs whitespace-nowrap pr-2">
                      {fmt(lineNet)}
                    </td>
                  )}
                  <td className="py-1.5 text-center">
                    {lines.length > 1 && !locked && (
                      <button onClick={() => onRemoveLine(idx)} className="text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 transition-colors text-base leading-none">×</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Totaalregel — REQ-TESTDATA-007: hidden while scenario-locked (this is
          a raw per-line quantity*unit_price*vat_percentage approximation,
          not canonical for a scenario with allowances/charges/document
          adjustments — the Financial Scenario Preview panel shows the real
          canonical totals instead). */}
      {!isDA && !locked && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <div className="text-sm space-y-1 min-w-56">
            <div className="flex justify-between text-gray-500 dark:text-gray-400">
              <span>{t('linesEditor.subtotal_net')}</span>
              <span className="font-mono">{fmt(net)}</span>
            </div>
            {vatByRate.length === 1 ? (
              <div className="flex justify-between text-gray-500 dark:text-gray-400">
                <span>{t('linesEditor.vat_rate_label', { rate: vatByRate[0].rate })}</span>
                <span className="font-mono">{fmt(vatByRate[0].tax)}</span>
              </div>
            ) : (
              vatByRate.map(({ rate, grondslag, tax }) => (
                <div key={rate} className="flex justify-between text-gray-500 dark:text-gray-400">
                  <span>{t('linesEditor.vat_rate_with_base', { rate, base: fmt(grondslag) })}</span>
                  <span className="font-mono">{fmt(tax)}</span>
                </div>
              ))
            )}
            <div className="flex justify-between font-semibold text-gray-800 dark:text-gray-200 border-t border-gray-200 dark:border-gray-700 pt-1">
              <span>{t('linesEditor.total_incl_vat')}</span>
              <span className="font-mono text-blue-600 dark:text-blue-400">{fmt(net + vat)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
